import type { ReactNode } from 'react';
import { motion, type Variants } from 'motion/react';
import clsx from 'clsx';

interface CardProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'feature' | 'pricing' | 'testimonial';
  highlighted?: boolean;
  onClick?: () => void;
}

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

export function Card({
  children,
  className,
  variant = 'default',
  highlighted = false,
  onClick,
}: CardProps) {
  const variantClasses = {
    default: 'card',
    feature: 'card hover:border-accent/20 border border-transparent',
    pricing: highlighted
      ? 'card border-2 border-accent relative'
      : 'card border border-gray-200',
    testimonial: 'card bg-cream',
  };

  return (
    <motion.div
      variants={cardVariants}
      className={clsx(variantClasses[variant], className)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {highlighted && variant === 'pricing' && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-sm font-semibold px-4 py-1 rounded-full">
          Most Popular
        </div>
      )}
      {children}
    </motion.div>
  );
}

// Feature card with icon
interface FeatureCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  className?: string;
}

export function FeatureCard({ icon, title, description, className }: FeatureCardProps) {
  return (
    <Card variant="feature" className={className}>
      <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center text-accent mb-4">
        {icon}
      </div>
      <h3 className="font-heading font-semibold text-lg mb-2">{title}</h3>
      <p className="text-muted">{description}</p>
    </Card>
  );
}

// Pricing card
interface PricingCardProps {
  name: string;
  description: string;
  price: number | 'contact';
  priceUnit: string;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
  highlighted?: boolean;
  className?: string;
}

export function PricingCard({
  name,
  description,
  price,
  priceUnit,
  features,
  ctaLabel,
  ctaHref,
  highlighted = false,
  className,
}: PricingCardProps) {
  return (
    <Card variant="pricing" highlighted={highlighted} className={clsx('flex flex-col', className)}>
      <div className="mb-6">
        <h3 className="font-heading font-bold text-xl mb-1">{name}</h3>
        <p className="text-muted text-sm">{description}</p>
      </div>

      <div className="mb-6">
        {price === 'contact' ? (
          <div className="text-3xl font-bold">Custom</div>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold">${price}</span>
            <span className="text-muted">/{priceUnit}</span>
          </div>
        )}
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
            <span className="text-sm">{feature}</span>
          </li>
        ))}
      </ul>

      <a
        href={ctaHref}
        className={clsx(
          'w-full text-center py-3 px-6 rounded-lg font-semibold transition-all',
          highlighted
            ? 'btn-primary'
            : 'btn-secondary'
        )}
      >
        {ctaLabel}
      </a>
    </Card>
  );
}

// Testimonial card
interface TestimonialCardProps {
  quote: string;
  author: string;
  role: string;
  company: string;
  imageUrl?: string;
  stat?: { label: string; value: string };
  className?: string;
}

export function TestimonialCard({
  quote,
  author,
  role,
  company,
  imageUrl,
  stat,
  className,
}: TestimonialCardProps) {
  return (
    <Card variant="testimonial" className={className}>
      {stat && (
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="text-3xl font-bold text-accent">{stat.value}</div>
          <div className="text-sm text-muted">{stat.label}</div>
        </div>
      )}
      <blockquote className="text-lg mb-4">&ldquo;{quote}&rdquo;</blockquote>
      <div className="flex items-center gap-3">
        {imageUrl && (
          <img
            src={imageUrl}
            alt={author}
            className="w-12 h-12 rounded-full object-cover"
          />
        )}
        <div>
          <div className="font-semibold">{author}</div>
          <div className="text-sm text-muted">
            {role}, {company}
          </div>
        </div>
      </div>
    </Card>
  );
}

// Simple check icon
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}
